import React, { lazy } from 'react';

// Wraps React.lazy so a tab that hits a missing page chunk recovers instead of
// leaving the user on a blank route or in a reload loop.
//
// 2026-09-24 post-mortem ("user management can't be loaded", ~5 reloads/s):
//   1. a chunk 404'd for a minute mid-deploy and the browser CACHED that 404
//      (the static-files rule sent an immutable cache header on every status);
//   2. in the minified bundle the importer has no "import('./pages/X')" text,
//      so the old guard key fell back to Date.now() — a new key on every load,
//      so "reload once" became "reload forever".
// Now: the guard key is a stable hash of the importer's own source; on a
// chunk error the failed URL is refetched with cache:'reload' (bypassing the
// cached 404) and the import retried; only then one automatic reload; and a
// visible "Reload" notice after that — never a blank page, never a loop.
const hashStr = (s) => { let h = 0; for (let i = 0; i < s.length; i++) { h = (h * 31 + s.charCodeAt(i)) | 0; } return (h >>> 0).toString(36); };

export function lazyWithReload(importer) {
  const key = `chunk-reload-${hashStr(importer.toString())}`;

  const ReloadNotice = () => (
    <div style={{ padding: '40px 20px', textAlign: 'center', color: '#57606a' }}>
      <p style={{ margin: '0 0 12px' }}>This page could not be loaded — a new version of the app was just published.</p>
      <button
        className="btn btn-primary"
        onClick={() => { try { sessionStorage.removeItem(key); } catch (e) { /* ignore */ } window.location.reload(); }}
      >
        Reload
      </button>
    </div>
  );

  const isChunkError = (error) => {
    const msg = String(error && error.message);
    return /ChunkLoadError/.test(String(error && error.name)) ||
      /Loading (CSS )?chunk [\w-]+ failed/.test(msg) ||
      /failed to fetch|unknown error occurred when fetching the script/i.test(msg);
  };

  return lazy(async () => {
    try {
      const mod = await importer();
      try { sessionStorage.removeItem(key); } catch (e) { /* ignore */ }
      return mod;
    } catch (error) {
      if (!isChunkError(error)) throw error;
      // A cached 404/failed response for the chunk URL: refresh that cache
      // entry and try the import once more before touching the page.
      const url = error && error.request;
      if (typeof url === 'string' && url) {
        try {
          await fetch(url, { cache: 'reload', credentials: 'same-origin' });
          const mod = await importer();
          try { sessionStorage.removeItem(key); } catch (e) { /* ignore */ }
          return mod;
        } catch (e) { /* fall through to the reload path */ }
      }
      let alreadyReloaded = false;
      try { alreadyReloaded = !!sessionStorage.getItem(key); sessionStorage.setItem(key, '1'); } catch (e) { alreadyReloaded = true; }
      if (!alreadyReloaded) {
        window.location.reload();
        return new Promise(() => {}); // reload takes over before this matters
      }
      return { default: ReloadNotice };
    }
  });
}
